import React, { useState, useRef } from 'react'

import styles from './FileImport.module.scss'

import { fqAlert } from '../NotificationManager/NotificationManager'

interface FileImportDropZoneProps {
    fileSizeLimit: number
    fileTypes?: string[]
    changeBackground?: boolean
}

const ImportDropZone = ({
    fileTypes,
    fileSizeLimit,
    changeBackground,
}: FileImportDropZoneProps) => {
    return (
        <div className={'centered'} style={{ backgroundColor: changeBackground ? '#F2F2F2' : '' }}>
            <div>
                <div className={'centered'}>
                    <span
                        className={'k-icon k-i-file-txt k-i-txt'}
                        style={{
                            fontSize: '3em',
                            color: 'var(--fadedText)',
                        }}
                    />
                </div>
                <div
                    className={'centered'}
                    style={{
                        color: 'var(--fadedText)',
                        flexDirection: 'column',
                        fontStyle: 'italic',
                        marginTop: 10,
                    }}
                >
                    <div style={{ textAlign: 'center' }}>
                        Drag and drop import file here
                        <br />
                        or click to browse
                    </div>
                    <ul style={{ listStyleType: 'disc', margin: '5px' }}>
                        {fileTypes && fileTypes.length > 0 && (
                            <li>
                                File type{fileTypes.length > 1 ? 's' : ''}: {fileTypes.join(', ')}
                            </li>
                        )}
                        <li>Maximum file size: {fileSizeLimit / 1000000}mb</li>
                    </ul>
                </div>
            </div>
        </div>
    )
}

interface FileImportProps {
    fileSizeLimit: number
    fileTypes?: string[]
    onDrop(fileData: string): void
    minHeight?: number
    changeBackground?: boolean
    setFileName?: React.Dispatch<React.SetStateAction<string>>
}

export const FileImport = ({
    onDrop,
    fileSizeLimit,
    fileTypes,
    minHeight,
    changeBackground,
    setFileName,
}: FileImportProps) => {
    const [fileData, setFileData] = useState<string>('')

    const onFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
        if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) return

        e.stopPropagation()
        e.preventDefault()
        readFile(e.dataTransfer.files[0])
    }

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return

        readFile(e.target.files[0])
    }

    const readFile = (file: File) => {
        if (setFileName) {
            setFileName(file.name)
        }
        if (file.size > fileSizeLimit) {
            let errorMessage = `${file.name} is too large. Max file size is ${(
                fileSizeLimit / 1000000
            ).toLocaleString(undefined, { maximumFractionDigits: 1 })}mb\n`
            fqAlert({ type: 'error', title: errorMessage })
            return
        }

        let reader = new FileReader()
        reader.addEventListener(
            'load',
            (e) => {
                if (!e.target || !e.target.result) return

                const sFileData = e.target.result as String
                const base64Start = sFileData.indexOf('base64,') + 7
                const base64Data = sFileData.substring(base64Start, sFileData.length)
                setFileData(base64Data)

                onDrop && onDrop(base64Data)
            },
            false
        )
        reader.readAsDataURL(file)
        return ''
    }

    const fileInputBrowse = useRef<HTMLInputElement>(null)
    const onClick = () => fileInputBrowse.current?.click()

    return (
        <>
            {!fileData && (
                <div
                    className={styles.dropZone}
                    onDrop={onFileDrop}
                    onClick={onClick}
                    data-testid='importDiv'
                    style={{
                        minHeight: minHeight,
                        backgroundColor: changeBackground ? '#F2F2F2' : '',
                    }}
                >
                    <div className={`${styles.fileImport} fileImport`}>
                        <ImportDropZone
                            fileSizeLimit={fileSizeLimit}
                            fileTypes={fileTypes}
                            changeBackground={changeBackground}
                        />
                    </div>
                    <input
                        id='fileInputBrowse'
                        type='file'
                        ref={fileInputBrowse}
                        onChange={onChange}
                    />
                </div>
            )}
        </>
    )
}

export default FileImport
